/**
 * The confirm page behind every turn-off link (S3-15). Opening it changes nothing; only the button
 * does. It loads in the browser, so a link scanner gets the shell and asks nothing of the server.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get, postJson } from '../../api/httpClient';
import { isBadLink, tokenFrom } from '../../util/emailLinks';

export const readLinkThunk = createAsyncThunk(
  'EmailTurnOffPage/readLink',
  (token, { rejectWithValue }) =>
    get('/api/email/turn-off/link', { token })
      .then(response => response.data.data)
      .catch(e => rejectWithValue(isBadLink(e) ? 'invalid' : 'failed'))
);

export const turnOffThunk = createAsyncThunk(
  'EmailTurnOffPage/turnOff',
  (token, { rejectWithValue }) =>
    postJson(`/api/email/turn-off?token=${encodeURIComponent(token)}`).catch(e =>
      rejectWithValue(isBadLink(e) ? 'invalid' : 'failed')
    )
);

const initialState = { status: 'loading', link: null, turnOffStatus: 'idle' };

const emailTurnOffPageSlice = createSlice({
  name: 'EmailTurnOffPage',
  initialState,
  reducers: {
    linkMissing: state => {
      state.status = 'invalid';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(readLinkThunk.pending, () => initialState)
      .addCase(readLinkThunk.fulfilled, (state, action) => {
        state.status = 'ready';
        state.link = action.payload;
      })
      .addCase(readLinkThunk.rejected, (state, action) => {
        state.status = action.payload === 'invalid' ? 'invalid' : 'failed';
      })
      .addCase(turnOffThunk.pending, state => {
        state.turnOffStatus = 'sending';
      })
      .addCase(turnOffThunk.fulfilled, state => {
        state.turnOffStatus = 'done';
      })
      .addCase(turnOffThunk.rejected, (state, action) => {
        if (action.payload === 'invalid') state.status = 'invalid';
        state.turnOffStatus = 'failed';
      });
  },
});

export default emailTurnOffPageSlice.reducer;

export const { linkMissing } = emailTurnOffPageSlice.actions;

export const loadData = (params, search) => dispatch => {
  const token = tokenFrom(search);
  return token ? dispatch(readLinkThunk(token)) : Promise.resolve(dispatch(linkMissing()));
};
